/**
 * RecordingWarningModal Component
 *
 * Displays a warning modal before starting audio recording for transcription.
 * Warns users that the transcription process may overwrite existing request data.
 *
 * Cross-platform compatible (web, iOS, Android)
 */

import React from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { AlertTriangle } from '../icons';

export interface RecordingWarningModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Callback when OK button is pressed (starts recording) */
  onConfirm: () => void;
  /** Callback when Cancel button is pressed (closes modal) */
  onCancel: () => void;
}

/**
 * Warning modal for audio recording
 *
 * @example
 * <RecordingWarningModal
 *   visible={showWarning}
 *   onConfirm={() => {
 *     setShowWarning(false);
 *     handleStartRecording();
 *   }}
 *   onCancel={() => setShowWarning(false)}
 * />
 */
export const RecordingWarningModal: React.FC<RecordingWarningModalProps> = ({
  visible,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onCancel}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Warning Icon */}
          <View style={styles.iconContainer}>
            <AlertTriangle size={48} color="#FF6B35" />
          </View>

          {/* Title */}
          <Text style={styles.title}>Recording Warning</Text>

          {/* Warning Message */}
          <Text style={styles.message}>
            Recording audio for transcription may result in overwriting current Request fields. Do you want to continue?
          </Text>

          {/* Button Container */}
          <View style={styles.buttonContainer}>
            {/* Cancel Button */}
            <TouchableOpacity
              testID="recording-warning-cancel"
              style={[styles.button, styles.cancelButton]}
              onPress={onCancel}
              activeOpacity={0.7}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            {/* OK Button */}
            <TouchableOpacity
              testID="recording-warning-confirm"
              style={[styles.button, styles.confirmButton]}
              onPress={onConfirm}
              activeOpacity={0.7}
            >
              <Text style={styles.confirmButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
      },
    }),
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1A1A1A',
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    fontSize: 14,
    color: '#666666',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  cancelButton: {
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666666',
  },
  confirmButton: {
    backgroundColor: '#FF6B35',
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
